import React from "react";
import { FleetrifyBgs } from "../../../assets/video";
import SuccessPopup from "../../../components/SuccessPopup";
import { FleetrifyInputField } from "../../../components/Input/input";
import { fleetrifyIcons } from "../../../assets/icons/icons";
import { CheckCircle, EyeOff } from "lucide-react";
import { FleetrifyButton } from "../../../components/Button/customButton";
import CarouselCard from "../../../components/Carousel";
import { usePasswordResetController } from "../../../controllers/sePasswordResetController.ts";

const PasswordReset = () => {
  const {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    isLoading,
    showSuccess,
    setShowSuccess,
    errorMessage,
    isFormValid,
    handleSubmit,
    goToLogin,
  } = usePasswordResetController();

  const slides = [
    { title: "Welcome to Fleetrify", description: "Login to manage logistics and fleet operations" },
    { title: "Real-time Fleet Insights", description: "Get live updates and analytics on every vehicle in your fleet" },
    { title: "Simplify Logistics Management", description: "Automate routing, tracking, and reporting — all in one platform" },
  ];

  // password rules for users
  const minLength = password.length >= 8;
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>[\]\\\/`~_\-+=;:]/.test(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  return (
    <div className="relative flex flex-col lg:flex-row min-h-screen items-center justify-center lg:bg-transparent bg-gray-100">
      {/* video section*/}
      <div className="relative w-full lg:w-[45%] h-screen lg:h-screen order-2 lg:order-none">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline>
          <source src={FleetrifyBgs} type="video/mp4" />
          Your browser does not support the video tag
        </video>
        <div className="absolute inset-0 bg-black opacity-50 z-10"></div>
        <div className="absolute z-20 inset-0 flex items-center justify-center">
          <CarouselCard slides={slides} />
        </div>
      </div>

      {/* reset form */}
      <div className="w-full lg:w-[55%] flex justify-center items-center absolute lg:static z-30 px-5 top-0 bottom-0 my-auto">
        <form
          onSubmit={handleSubmit}
          className="w-[430px] sm:w-[340px] md:w-[500px] lg:w-[430px] max-w-full bg-white px-4 py-6 rounded"
        >
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-center text-[#143887]">Reset Password</h2>

            {errorMessage && <p className="text-red-500 text-sm text-center">{errorMessage}</p>}

            {/* password field */}
            <div className="space-y-4">
              <div>
                <label className="block text-[#4F4F4F] mb-1 font-bold text-base">New Password</label>
                <FleetrifyInputField
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Enter new password"
                  className="mt-1"
                  icon={
                    <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="focus:outline-none">
                      {showPassword ? <EyeOff size={16} color="#333333" /> : fleetrifyIcons.viewIcon}
                    </button>
                  }
                  aria-describedby="pw-requirements"
                />
                <div id="pw-requirements" className="mt-2 text-sm">
                  {!minLength && password.length > 0 && <p className="text-red-500">Password must be at least 8 characters.</p>}
                  {!hasNumber && password.length > 0 && <p className="text-red-500">Password must include at least one number (0-9).</p>}
                  {!hasSpecial && password.length > 0 && <p className="text-red-500">Password must include at least one special character.</p>}
                </div>
              </div>

              <div>
                <label className="block text-[#4F4F4F] mb-1 font-bold text-base">Confirm Password</label>
                <FleetrifyInputField
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  placeholder="Re-enter password"
                  className="mt-1"
                  icon={
                    <button type="button" onClick={() => setShowConfirmPassword((prev) => !prev)} className="focus:outline-none">
                      {showConfirmPassword ? <EyeOff size={16} color="#333333" /> : fleetrifyIcons.viewIcon}
                    </button>
                  }
                />
                {!passwordsMatch && confirmPassword.length > 0 && <p className="text-red-500 text-sm mt-1">Passwords do not match.</p>}
              </div>
            </div>
          </div>

          {/* submit button */}
          <FleetrifyButton
            type="submit"
            disabled={!isFormValid || isLoading}
            className={`w-full mt-6 ${!isFormValid || isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isLoading ? "Verifying..." : "Verify"}
          </FleetrifyButton>
        </form>

        {/* success popup */}
        {showSuccess && (
          <SuccessPopup
            onClose={() => setShowSuccess(false)}
            content={
              <>
                <div className="bg-white p-4 rounded-full mb-4">
                  <CheckCircle className="text-green-600 w-10 h-10" />
                </div>
                <p className="text-[#4F4F4F] font-medium text-base text-center">
                  You have successfully reset your password
                </p>
              </>
            }
            actions={
              <button
                onClick={goToLogin}
                className="bg-[#143887] text-white px-2 py-2 rounded-md w-full md:w-1/2 text-sm hover:bg-[#1a4aa0] transition"
              >
                Back to Login
              </button>
            }
          />
        )}
      </div>
    </div>
  );
};

export default PasswordReset;
